import React, { Children, Component } from 'react';
import { createPortal } from 'react-dom';

import { element, object } from 'prop-types';

import { MenuOptionAPI, MenuOptionType } from '@philips-emr/tws-components-api';
import { Alignment, Direction, Placement } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { DataContext, RouteContext } from '@philips-emr/tws-core/lib/component/context';
import { arrayOf, bool, eventListener, expression, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import HistoryService, { matchRouteWith } from '@philips-emr/tws-core/lib/services/history-service';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isEnumValid, isFunction, isMobile, propagateProps } from '@philips-emr/tws-utils';

import { Position } from '..';

import './menu-option.scss';

const acceptedTypes = [
  ComponentType.NAVIGATION,
];

@AwesomeComponent({ type: ComponentType.NAVIGATION, api: MenuOptionAPI })
class MenuOption extends Component {

  static contextType = DataContext.Context;

  static propTypes = {
    badge: string,
    baseName: string.readOnly(),
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    description: expression,
    direction: oneOf(enumValues(Direction)).readOnly(),
    dockedSubmenu: bool.readOnly(),
    icon: string,
    iconTarget: oneOfType([element, object]).notEnumerable(),
    label: expression,
    onClick: eventListener,
    onDoubleClick: eventListener,
    onContextMenuClick: eventListener,
    onOpen: eventListener,
    onClose: eventListener,
    open: bool,
    root: oneOfType([element, object]).notEnumerable(),
    route: string,
    type: oneOf(enumValues(MenuOptionType)).readOnly(),
    uuid: string,
    visible: bool,
  };

  static defaultProps = {
    label: 'Sample label',
    type: MenuOptionType.PRIMARY,
    baseName: '',
    visible: true,
  };

  state = {
    showSubmenu: false,
  };

  getCompleteRoute(routeContext) {
    const { baseName, route } = this.props;
    return routeContext ? routeContext.nestedRoute(`${baseName}${route}`) : `${baseName}${route}`;
  }

  isVertical() {
    return this.props.direction === Direction.VERTICAL;
  }

  isCurrentRoute(routeContext) {
    const { route } = this.props;

    const completeRoute = this.getCompleteRoute(routeContext, route);

    return matchRouteWith(completeRoute);
  }

  isSubmenuDocked() {
    return !this.isVertical() && this.props.dockedSubmenu;
  }

  hasChildren() {
    return Children.count(this.props.children) > 0;
  }

  shouldTriggerClose() {
    return !this.hasChildren();
  }

  onClick = (routeContext) => (event) => {
    if (event.isDefaultPrevented()) {
      return;
    }

    event.preventDefault();

    const { onClick, onOpen, route } = this.props;

    if (!this.hasChildren() && route) {
      const completeRoute = this.getCompleteRoute(routeContext);
      HistoryService.push(completeRoute, this.context);
    }

    isFunction(onClick) && onClick({ value: this.shouldTriggerClose() });
    if (this.hasChildren()) {
      const callback = () => {
        const { showSubmenu } = this.state;

        if (showSubmenu) {
          isFunction(onOpen) && onOpen();
        }
      };

      this.setState(({ showSubmenu }) => ({ showSubmenu: !showSubmenu }), callback);
    }
  };

  optionRef = (optionElement) => {
    this.setState({ optionElement });
  };

  getClassName(routeContext) {
    const { type, direction } = this.props;

    return classNames({
      'c-menu-option': true,
      'is-active': this.isCurrentRoute(routeContext),
      [`c-menu-option--${type}`]: isEnumValid(MenuOptionType, type),
      [`c-menu-option--${direction}`]: isEnumValid(Direction, direction),
    });
  }

  getHoverEvents() {
    return {
      onMouseEnter: () => this.setState({ showSubmenu: true }),
      onMouseLeave: () => this.setState({ showSubmenu: false }),
    };
  }

  renderSubmenu() {
    if (!this.hasChildren()) {
      return null;
    }

    const onCloseFn = (event) => {
      if (isMobile()) {
        this.setState({ showSubmenu: false });
      }

      isFunction(this.props.onClose) && this.props.onClose(event);
    };

    const children = propagateProps(this.props.children, {
      baseName: this.props.route,
      dockedSubmenu: this.isSubmenuDocked(),
      iconTarget: this.props.iconTarget,
      onClose: onCloseFn,
      submenu: true,
    });

    if (isMobile()) {
      return createPortal(children, this.props.root);
    }

    return children;
  }

  renderChildren() {
    const { root, open } = this.props;
    const { optionElement, showSubmenu } = this.state;

    if (!open && !showSubmenu) {
      return null;
    }

    if (isMobile()) {
      return this.renderSubmenu();
    }

    const placement = this.isVertical() ? Placement.BOTTOM : Placement.RIGHT;
    const alignment = !this.isVertical() ? Alignment.TOP : Alignment.LEFT;
    const target = this.isSubmenuDocked() ? root : optionElement;

    return (
      <Position
        target={target}
        placement={placement}
        alignment={alignment}
        matchTargetHeight={this.isSubmenuDocked()}
        force={this.isSubmenuDocked()}
        updateOnScroll={true}>
        {this.renderSubmenu()}
      </Position>
    );
  }

  onDoubleClick = () => {
    const { onDoubleClick } = this.props;

    isFunction(onDoubleClick) && onDoubleClick();
  };

  onContextMenuClick = () => {
    const { onContextMenuClick } = this.props;

    isFunction(onContextMenuClick) && onContextMenuClick();
  };

  render() {
    const { label, description, badge, uuid, icon, visible } = this.props;
    const hoverEvents = !isMobile() && this.hasChildren() ? this.getHoverEvents() : {};
    const Container = ContainerProvider.provide(this);
    if (!visible) {
      return null;
    }
    return (
      <RouteContext.Context.Consumer>
        {routeContext => (

          <Container
            {...hoverEvents}
            acceptedTypes={acceptedTypes}
            className={this.getClassName(routeContext)}
            onClick={this.onClick(routeContext)}
            onDoubleClick={this.onDoubleClick}
            onContextMenu={this.onContextMenuClick}
            uuid={uuid}
            x-contrast="bg bd">

            {icon && (
              <img className="c-menu-option__icon" src={icon} x-contrast="bg" />
            )}

            <div className="c-menu-option__information" x-contrast="bg">
              <span className="c-menu-option__label u-truncate" x-contrast="bg cl">{msg(label)}</span>

              {description && (
                <span className="c-menu-option__desc u-truncate" x-contrast="bg">{msg(description)}</span>
              )}
            </div>

            {badge && (
              <span className="c-menu-option__badge" x-contrast="bg">{badge}</span>
            )}

            {this.hasChildren() && (
              <span className="c-menu-option__arrow" x-contrast="bg"></span>
            )}

            <div className="u-backdrop" ref={this.optionRef}></div>

            {this.renderChildren()}
          </Container>
        )}
      </RouteContext.Context.Consumer>
    );
  }
}

export default MenuOption;
